// frontend/src/reducers/cartReducers.js

import * as actionTypes from "../constants/cartConstants";

const STOCK_STATUS_FALLBACK = "available";
const STOCK_SEVERITY_FALLBACK = "normal";

const CHECKOUT_BLOCKING_STOCK_STATUSES = new Set([
  "out_of_stock",
  "requested_qty_exceeds_available",
  "shared_stock_blocked_by_cart",
  "unavailable",
]);

const ADD_BLOCKING_STOCK_STATUSES = new Set([
  ...CHECKOUT_BLOCKING_STOCK_STATUSES,
  "max_in_cart",
  "shared_stock_exhausted_in_cart",
]);

const NON_CHECKOUT_BLOCKING_BASKET_STATUSES = new Set([
  "max_in_cart",
  "shared_stock_exhausted_in_cart",
]);

const CART_STOCK_STATUS_LABELS = Object.freeze({
  available: "Available",
  out_of_stock: "Sold out",
  max_in_cart: "In your basket",
  requested_qty_exceeds_available: "Limited stock",
  shared_stock_exhausted_in_cart: "In your basket",
  shared_stock_blocked_by_cart: "Choose another cut",
  unavailable: "Unavailable",
});

/* -------------------------------------------------------------------------- */
/*                                Initial state                               */
/* -------------------------------------------------------------------------- */

function createInitialSummary() {
  return {
    itemCount: 0,
    distinctItemCount: 0,
    subtotal: 0,
    discountTotal: 0,
    appliedPromoCode: null,
    hasUnavailableItems: false,
    hasPriceChanges: false,
    readyForCheckout: false,
    ui: {
      status: "empty",
      hasItems: false,
      blockingIssueCount: 0,
      unavailableItemCount: 0,
      priceChangedItemCount: 0,
      actions: {
        canCheckout: false,
        canRemoveUnavailableItems: false,
        canRemovePriceChangedItems: false,
      },
    },
  };
}

function createInitialValidation() {
  return {
    ok: true,
    empty: true,
    issues: [],
    message: "",
  };
}

function createInitialQuote() {
  return {
    currency: "GBP",
    subtotal: 0,
    shippingTotal: 0,
    taxTotal: 0,
    discountTotal: 0,
    appliedPromoCode: null,
    grandTotal: 0,

    fulfillmentMethod: "delivery",
    shippingCountry: "",
    deliveryOption: null,

    readyForCheckout: false,
    hasUnavailableItems: false,
    hasPriceChanges: false,
    itemCount: 0,
    distinctItemCount: 0,

    messages: {
      shipping: "",
      total: "",
    },
  };
}

function createInitialCheckout() {
  return {
    fulfillmentMethod: "delivery",

    customer: {
      name: "",
      email: "",
      phone: "",
    },

    shippingAddress: {
      fullName: "",
      company: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      phone: "",
    },

    pickupDetails: {
      appointmentOnly: true,
      date: "",
      dayLabel: "",
      timeSlot: "",
      reference: "",
      instructions: "",
    },

    billing: {
      useShippingAsBilling: true,
      address: {
        fullName: "",
        company: "",
        line1: "",
        line2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
        phone: "",
      },
    },

    acknowledgements: {
      textileNoReturnAccepted: false,
      textileNoReturnAcceptedAt: null,
      standardReturnsAccepted: false,
      standardReturnsAcceptedAt: null,
    },

    paymentMethod: "",
    paymentProvider: "",
    notes: "",
    updatedAt: null,
  };
}

function createInitialState() {
  return {
    loading: false,

    cart: null,
    summary: createInitialSummary(),
    validation: createInitialValidation(),
    quote: null,
    checkout: null,

    success: false,
    error: null,
    errorCode: "",
    errorMeta: null,
    message: "",
    issues: [],
    lastAction: "",

    pending: {
      addKeys: {},
      itemIds: {},

      summary: false,
      refresh: false,
      validate: false,
      quote: false,
      merge: false,
      clear: false,
      replace: false,
      promoCode: false,

      checkout: false,
      checkoutFulfillment: false,
      checkoutCustomer: false,
      checkoutShippingAddress: false,
      checkoutPickupDetails: false,
      checkoutBilling: false,
      checkoutAcknowledgements: false,
      checkoutPayment: false,
      checkoutNotes: false,

      cleanupUnavailable: false,
      cleanupPriceChanged: false,
    },
  };
}

const initialState = createInitialState();

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function toNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

function toNonNegativeNumber(value, fallback = 0) {
  return Math.max(0, toNumber(value, fallback));
}

function toNonNegativeInteger(value, fallback = 0) {
  const number = Math.floor(Number(value));

  if (!Number.isFinite(number) || number < 0) return fallback;

  return number;
}

function roundMoney(value) {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) return 0;

  return Math.round(number * 100) / 100;
}

function normalizeString(value = "") {
  return String(value ?? "").trim();
}

function normalizeUpperString(value = "") {
  return normalizeString(value).toUpperCase();
}

function normalizeLowerString(value = "") {
  return normalizeString(value).toLowerCase();
}

function normalizeObjectId(value = null) {
  if (value === undefined || value === null) return null;

  return value;
}

function containsUnsafeCustomerStockCopy(value = "") {
  const text = normalizeLowerString(value);

  if (!text) return false;

  return (
    text.includes("reserved") ||
    text.includes("temporarily reserved") ||
    text.includes("allocated") ||
    text.includes("held") ||
    text.includes("held for")
  );
}

function customerSafeText(value = "") {
  const text = normalizeString(value);

  return containsUnsafeCustomerStockCopy(text) ? "" : text;
}

function normalizeSharedStock(sharedStock = {}) {
  return {
    isShared: Boolean(sharedStock?.isShared),
    baseMetres: toNonNegativeNumber(sharedStock?.baseMetres, 0),
    consumedMetres: toNonNegativeNumber(sharedStock?.consumedMetres, 0),
    remainingMetres: toNonNegativeNumber(sharedStock?.remainingMetres, 0),
    consumedByThisLineMetres: toNonNegativeNumber(
      sharedStock?.consumedByThisLineMetres,
      0,
    ),
    consumedByOtherLinesMetres: toNonNegativeNumber(
      sharedStock?.consumedByOtherLinesMetres,
      0,
    ),
  };
}

function deriveSharedStockStatusFromReason(reason = "") {
  const normalizedReason = normalizeLowerString(reason);

  if (
    normalizedReason.includes("other cuts") ||
    normalizedReason.includes("other lines") ||
    normalizedReason.includes("blocked")
  ) {
    return "shared_stock_blocked_by_cart";
  }

  return "shared_stock_exhausted_in_cart";
}

function deriveStockStatus(item = {}) {
  const explicitStatus = normalizeLowerString(
    item?.stockStatus || item?.status,
  );

  if (explicitStatus) return explicitStatus;

  const unavailableCode = normalizeUpperString(item?.unavailableCode);
  const stockStatusCode = normalizeUpperString(item?.stockStatusCode);
  const statusCode = normalizeUpperString(item?.statusCode);
  const code = stockStatusCode || statusCode || unavailableCode;

  if (!code) return STOCK_STATUS_FALLBACK;
  if (code === "MAX_IN_CART") return "max_in_cart";

  if (
    code === "REQUESTED_QTY_EXCEEDS_AVAILABLE" ||
    code === "INSUFFICIENT_STOCK"
  ) {
    return "requested_qty_exceeds_available";
  }

  if (code === "SHARED_STOCK_BLOCKED_BY_CART") {
    return "shared_stock_blocked_by_cart";
  }

  if (code === "SHARED_STOCK_EXHAUSTED_IN_CART") {
    return "shared_stock_exhausted_in_cart";
  }

  if (code === "INSUFFICIENT_SHARED_STOCK") {
    return deriveSharedStockStatusFromReason(
      item?.statusReason ||
        item?.stockStatusReason ||
        item?.unavailableReason ||
        item?.disabledReason,
    );
  }

  if (code === "OUT_OF_STOCK") return "out_of_stock";
  if (code === "UNAVAILABLE") return "unavailable";

  return "unavailable";
}

function deriveStockSeverity(item = {}) {
  const explicitSeverity = normalizeLowerString(item?.stockSeverity);
  const status = deriveStockStatus(item);

  if (CHECKOUT_BLOCKING_STOCK_STATUSES.has(status)) return "blocked";

  if (NON_CHECKOUT_BLOCKING_BASKET_STATUSES.has(status)) {
    return explicitSeverity || "info";
  }

  if (explicitSeverity) return explicitSeverity;

  if (item?.isAvailable === false) return "blocked";

  return STOCK_SEVERITY_FALLBACK;
}

function getCartItemStatusReason(item = {}) {
  return customerSafeText(
    item?.statusReason ||
      item?.stockStatusReason ||
      item?.unavailableReason ||
      item?.disabledReason ||
      "",
  );
}

function getCartItemDisabledReason(item = {}, stockStatus = "") {
  const explicitReason = getCartItemStatusReason(item);

  if (explicitReason) return explicitReason;

  switch (stockStatus) {
    case "out_of_stock":
      return "This item is currently out of stock.";

    case "max_in_cart":
      return "This item is already in your basket.";

    case "requested_qty_exceeds_available":
      return "The selected quantity is no longer available.";

    case "shared_stock_exhausted_in_cart":
      return "This cut is already in your basket. No more can be added from this shared roll.";

    case "shared_stock_blocked_by_cart":
      return "Choose another cut from this shared roll.";

    case "unavailable":
      return "This item is currently unavailable.";

    default:
      return "";
  }
}

function getCartItemStatusLabel(item = {}, stockStatus = "") {
  const explicitLabel = customerSafeText(item?.statusLabel);

  if (explicitLabel) return explicitLabel;

  const stockSeverity = normalizeLowerString(item?.stockSeverity);

  if (stockStatus === "available" && stockSeverity === "low") {
    return "Low stock";
  }

  return CART_STOCK_STATUS_LABELS[stockStatus] || "Unavailable";
}

function itemBlocksCheckout(item = {}) {
  return Boolean(item?.isCheckoutBlocking);
}

function normalizeCartUi(ui = {}, fallbackItems = []) {
  const items = Array.isArray(fallbackItems) ? fallbackItems : [];

  const unavailableItemCount = items.filter(itemBlocksCheckout).length;

  const priceChangedItemCount = items.filter(
    (item) => item?.priceChanged === true,
  ).length;

  const blockingIssueCount = unavailableItemCount + priceChangedItemCount;
  const hasItems = items.length > 0;
  const canCheckout = hasItems && blockingIssueCount === 0;

  return {
    status: normalizeString(
      ui?.status || (canCheckout ? "ready" : hasItems ? "attention" : "empty"),
    ),

    hasItems: hasOwn(ui, "hasItems") ? Boolean(ui?.hasItems) : hasItems,

    blockingIssueCount: hasOwn(ui, "blockingIssueCount")
      ? toNonNegativeInteger(ui?.blockingIssueCount, 0)
      : blockingIssueCount,

    unavailableItemCount: hasOwn(ui, "unavailableItemCount")
      ? toNonNegativeInteger(ui?.unavailableItemCount, 0)
      : unavailableItemCount,

    priceChangedItemCount: hasOwn(ui, "priceChangedItemCount")
      ? toNonNegativeInteger(ui?.priceChangedItemCount, 0)
      : priceChangedItemCount,

    actions: {
      canCheckout: hasOwn(ui?.actions, "canCheckout")
        ? Boolean(ui?.actions?.canCheckout) && canCheckout
        : canCheckout,

      canRemoveUnavailableItems: hasOwn(
        ui?.actions,
        "canRemoveUnavailableItems",
      )
        ? Boolean(ui?.actions?.canRemoveUnavailableItems) &&
          unavailableItemCount > 0
        : unavailableItemCount > 0,

      canRemovePriceChangedItems: hasOwn(
        ui?.actions,
        "canRemovePriceChangedItems",
      )
        ? Boolean(ui?.actions?.canRemovePriceChangedItems) &&
          priceChangedItemCount > 0
        : priceChangedItemCount > 0,
    },
  };
}

function normalizeCheckout(checkout = null) {
  const source = checkout || createInitialCheckout();

  const paymentMethod = normalizeLowerString(
    source?.paymentMethod ||
      source?.paymentProvider ||
      source?.payment?.method ||
      source?.payment?.provider,
  );

  return {
    fulfillmentMethod:
      normalizeLowerString(source?.fulfillmentMethod) || "delivery",

    customer: {
      name: normalizeString(source?.customer?.name),
      email: normalizeLowerString(source?.customer?.email),
      phone: normalizeString(source?.customer?.phone),
    },

    shippingAddress: {
      fullName: normalizeString(source?.shippingAddress?.fullName),
      company: normalizeString(source?.shippingAddress?.company),
      line1: normalizeString(source?.shippingAddress?.line1),
      line2: normalizeString(source?.shippingAddress?.line2),
      city: normalizeString(source?.shippingAddress?.city),
      state: normalizeString(source?.shippingAddress?.state),
      postalCode: normalizeString(source?.shippingAddress?.postalCode),
      country: normalizeUpperString(source?.shippingAddress?.country),
      phone: normalizeString(source?.shippingAddress?.phone),
    },

    pickupDetails: {
      appointmentOnly:
        typeof source?.pickupDetails?.appointmentOnly === "boolean"
          ? source.pickupDetails.appointmentOnly
          : true,
      date: normalizeString(source?.pickupDetails?.date),
      dayLabel: normalizeString(source?.pickupDetails?.dayLabel),
      timeSlot: normalizeString(source?.pickupDetails?.timeSlot),
      reference: normalizeString(source?.pickupDetails?.reference),
      instructions: normalizeString(source?.pickupDetails?.instructions),
    },

    billing: {
      useShippingAsBilling:
        typeof source?.billing?.useShippingAsBilling === "boolean"
          ? source.billing.useShippingAsBilling
          : true,
      address: {
        fullName: normalizeString(source?.billing?.address?.fullName),
        company: normalizeString(source?.billing?.address?.company),
        line1: normalizeString(source?.billing?.address?.line1),
        line2: normalizeString(source?.billing?.address?.line2),
        city: normalizeString(source?.billing?.address?.city),
        state: normalizeString(source?.billing?.address?.state),
        postalCode: normalizeString(source?.billing?.address?.postalCode),
        country: normalizeUpperString(source?.billing?.address?.country),
        phone: normalizeString(source?.billing?.address?.phone),
      },
    },

    acknowledgements: {
      textileNoReturnAccepted: Boolean(
        source?.acknowledgements?.textileNoReturnAccepted,
      ),
      textileNoReturnAcceptedAt:
        source?.acknowledgements?.textileNoReturnAcceptedAt || null,

      standardReturnsAccepted: Boolean(
        source?.acknowledgements?.standardReturnsAccepted,
      ),
      standardReturnsAcceptedAt:
        source?.acknowledgements?.standardReturnsAcceptedAt || null,
    },

    paymentMethod,
    paymentProvider: paymentMethod,
    notes: normalizeString(source?.notes || source?.orderNote),
    updatedAt: source?.updatedAt || null,
  };
}

function normalizeCartItem(item = {}) {
  const qty = toNonNegativeInteger(item?.qty, 0);
  const unitPrice = roundMoney(item?.unitPrice || 0);
  const fallbackLineTotal = roundMoney(unitPrice * qty);

  const maxQty = toNonNegativeInteger(
    hasOwn(item, "maxQty") ? item?.maxQty : item?.availableAtRefresh,
    0,
  );

  const remainingAddableQty = hasOwn(item, "remainingAddableQty")
    ? toNonNegativeInteger(item?.remainingAddableQty, 0)
    : Math.max(0, maxQty - qty);

  const stockStatus = deriveStockStatus(item);
  const checkoutBlocking = CHECKOUT_BLOCKING_STOCK_STATUSES.has(stockStatus);
  const addBlocking = ADD_BLOCKING_STOCK_STATUSES.has(stockStatus);

  const backendUnavailable =
    typeof item?.isAvailable === "boolean" ? item.isAvailable === false : false;

  const basketLimited = NON_CHECKOUT_BLOCKING_BASKET_STATUSES.has(stockStatus);

  const isAvailable = checkoutBlocking
    ? false
    : basketLimited
      ? true
      : !backendUnavailable;

  const rawCanIncrement = hasOwn(item, "canIncrement")
    ? Boolean(item?.canIncrement)
    : maxQty <= 0
      ? true
      : qty < maxQty;

  const rawCanAddMore = hasOwn(item, "canAddMore")
    ? Boolean(item?.canAddMore)
    : maxQty <= 0
      ? true
      : remainingAddableQty > 0;

  const rawCanAddToCart = hasOwn(item, "canAddToCart")
    ? Boolean(item?.canAddToCart)
    : rawCanAddMore;

  const canAddMore = isAvailable && !addBlocking && rawCanAddMore;
  const canIncrement = isAvailable && !addBlocking && rawCanIncrement;
  const canAddToCart = isAvailable && !addBlocking && rawCanAddToCart;

  return {
    ...item,

    _id: normalizeString(item?._id || item?.id),
    id: normalizeString(item?.id || item?._id),
    lineKey: normalizeString(item?.lineKey),

    productId: normalizeObjectId(item?.productId),
    shopKey: normalizeString(item?.shopKey),
    shopFamily: normalizeLowerString(item?.shopFamily),
    productType: normalizeLowerString(item?.productType),
    itemType: normalizeLowerString(item?.itemType),

    productName: normalizeString(item?.productName),
    variantName: normalizeString(item?.variantName),

    sku: normalizeUpperString(item?.sku),
    variantSku: normalizeUpperString(item?.variantSku),
    variantType: normalizeLowerString(item?.variantType),

    image: normalizeString(item?.image),

    unitPrice,
    qty,
    lineTotal: hasOwn(item, "lineTotal")
      ? roundMoney(item?.lineTotal)
      : fallbackLineTotal,

    unitLabel: normalizeString(item?.unitLabel),
    unitValue: toNonNegativeNumber(item?.unitValue, 0),
    totalUnits: toNonNegativeNumber(item?.totalUnits, qty),
    totalLength: toNonNegativeNumber(item?.totalLength, 0),
    itemsPerPack: Math.max(1, toNonNegativeInteger(item?.itemsPerPack, 1)),

    color: normalizeString(item?.color),
    size: normalizeString(item?.size),
    material: normalizeString(item?.material),
    length: normalizeString(item?.length),

    availableAtRefresh: toNonNegativeInteger(item?.availableAtRefresh, 0),
    maxQty,
    remainingAddableQty,

    canIncrement,
    canAddMore,
    canAddToCart,

    isAvailable,
    isCheckoutBlocking: checkoutBlocking,
    isAddBlocking: addBlocking,

    unavailableCode: normalizeUpperString(item?.unavailableCode),
    unavailableReason: customerSafeText(item?.unavailableReason),

    stockStatus,
    stockStatusCode: normalizeUpperString(
      item?.stockStatusCode ||
        item?.statusCode ||
        item?.unavailableCode ||
        item?.code,
    ),
    stockStatusReason: getCartItemStatusReason(item),
    statusReason: getCartItemStatusReason(item),
    disabledReason: getCartItemDisabledReason(item, stockStatus),
    statusLabel: getCartItemStatusLabel(item, stockStatus),

    stockSeverity: deriveStockSeverity({
      ...item,
      stockStatus,
      isAvailable,
    }),

    isInBasket: qty > 0,
    isMaxedInCart: stockStatus === "max_in_cart",
    isSoldOut: stockStatus === "out_of_stock",
    isUnavailable: stockStatus === "unavailable",
    isSharedStockBlocked: stockStatus === "shared_stock_blocked_by_cart",
    isSharedStockInBasket: stockStatus === "shared_stock_exhausted_in_cart",
    isRequestedQtyUnavailable:
      stockStatus === "requested_qty_exceeds_available",

    sharedStock: normalizeSharedStock(item?.sharedStock),

    returnPolicyType: normalizeUpperString(item?.returnPolicyType),
    normalReturnAllowed: Boolean(item?.normalReturnAllowed),
    faultClaimAllowed:
      typeof item?.faultClaimAllowed === "boolean"
        ? Boolean(item.faultClaimAllowed)
        : true,
    cutStatus: normalizeLowerString(item?.cutStatus),
    cancellationWindowHours: toNonNegativeNumber(
      item?.cancellationWindowHours,
      0,
    ),
    returnRequestWindowDays: toNonNegativeNumber(
      item?.returnRequestWindowDays,
      0,
    ),
    returnShipWindowDays: toNonNegativeNumber(item?.returnShipWindowDays, 0),

    priceChanged: Boolean(item?.priceChanged),
    previousUnitPrice: roundMoney(item?.previousUnitPrice || 0),

    addedAt: item?.addedAt || null,
    updatedAt: item?.updatedAt || null,
  };
}

function normalizeCart(cart) {
  if (!cart) return null;

  const items = Array.isArray(cart?.items)
    ? cart.items.map(normalizeCartItem)
    : [];

  const checkout = cart?.checkout ? normalizeCheckout(cart.checkout) : null;

  const derivedHasUnavailableItems = items.some(itemBlocksCheckout);

  const hasUnavailableItems = hasOwn(cart, "hasUnavailableItems")
    ? Boolean(cart?.hasUnavailableItems) && derivedHasUnavailableItems
    : derivedHasUnavailableItems;

  const hasPriceChanges = hasOwn(cart, "hasPriceChanges")
    ? Boolean(cart?.hasPriceChanges)
    : items.some((item) => item.priceChanged);

  const itemCount = hasOwn(cart, "itemCount")
    ? toNonNegativeInteger(cart?.itemCount, 0)
    : items.reduce((sum, item) => sum + Number(item.qty || 0), 0);

  const distinctItemCount = hasOwn(cart, "distinctItemCount")
    ? toNonNegativeInteger(cart?.distinctItemCount, 0)
    : items.length;

  const subtotal = hasOwn(cart, "subtotal")
    ? roundMoney(cart?.subtotal || 0)
    : roundMoney(
        items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0),
      );

  const discountTotal = roundMoney(cart?.discountTotal || 0);
  const appliedPromoCode = cart?.appliedPromoCode || null;

  const readyForCheckout = hasOwn(cart, "readyForCheckout")
    ? Boolean(cart?.readyForCheckout) &&
      items.length > 0 &&
      !hasUnavailableItems &&
      !hasPriceChanges
    : items.length > 0 && !hasUnavailableItems && !hasPriceChanges;

  return {
    ...cart,

    id: cart?.id || cart?._id || null,
    _id: cart?._id || cart?.id || null,

    user: cart?.user || null,
    currency: normalizeUpperString(cart?.currency || "GBP") || "GBP",

    itemCount,
    distinctItemCount,
    subtotal,
    discountTotal,
    appliedPromoCode,

    hasUnavailableItems,
    hasPriceChanges,
    readyForCheckout,

    policy: cart?.policy || {},
    ui: normalizeCartUi(cart?.ui, items),

    items,
    checkout,

    expiresAt: cart?.expiresAt || null,
    createdAt: cart?.createdAt || null,
    updatedAt: cart?.updatedAt || null,
    lastActivityAt: cart?.lastActivityAt || null,
  };
}

function buildSummaryFromCart(cart) {
  if (!cart) return createInitialSummary();

  return {
    itemCount: toNonNegativeInteger(cart?.itemCount, 0),
    distinctItemCount: toNonNegativeInteger(cart?.distinctItemCount, 0),
    subtotal: roundMoney(cart?.subtotal || 0),
    discountTotal: roundMoney(cart?.discountTotal || 0),
    appliedPromoCode: cart?.appliedPromoCode || null,
    hasUnavailableItems: Boolean(cart?.hasUnavailableItems),
    hasPriceChanges: Boolean(cart?.hasPriceChanges),
    readyForCheckout: Boolean(cart?.readyForCheckout),
    ui: normalizeCartUi(cart?.ui, cart?.items),
  };
}

function buildSummaryFromPayload(payload = {}) {
  const sourceCart = payload?.cart ? normalizeCart(payload.cart) : null;

  if (sourceCart) return buildSummaryFromCart(sourceCart);

  const items = Array.isArray(payload?.items)
    ? payload.items.map(normalizeCartItem)
    : [];

  const hasUnavailableItems = items.some(itemBlocksCheckout);
  const hasPriceChanges = items.some((item) => item.priceChanged);

  return {
    itemCount: toNonNegativeInteger(payload?.itemCount, 0),
    distinctItemCount: toNonNegativeInteger(payload?.distinctItemCount, 0),
    subtotal: roundMoney(payload?.subtotal || 0),
    discountTotal: roundMoney(payload?.discountTotal || 0),
    appliedPromoCode: payload?.appliedPromoCode || null,
    hasUnavailableItems,
    hasPriceChanges,
    readyForCheckout:
      items.length > 0 && !hasUnavailableItems && !hasPriceChanges,
    ui: normalizeCartUi(payload?.ui, items),
  };
}

function buildCheckoutFromCart(cart) {
  return cart?.checkout ? normalizeCheckout(cart.checkout) : null;
}

function normalizeDeliveryOption(option = null) {
  if (!option) return null;

  return {
    id: normalizeString(option?.id),
    label: normalizeString(option?.label),
    description: normalizeString(option?.description),
    countries: Array.isArray(option?.countries) ? option.countries : [],
    price: roundMoney(option?.price || 0),
  };
}

function normalizeQuote(quote = null) {
  if (!quote) return createInitialQuote();

  return {
    currency: normalizeUpperString(quote?.currency || "GBP") || "GBP",

    subtotal: roundMoney(quote?.subtotal || 0),
    shippingTotal: roundMoney(quote?.shippingTotal || 0),
    taxTotal: roundMoney(quote?.taxTotal || 0),
    discountTotal: roundMoney(quote?.discountTotal || 0),
    appliedPromoCode: quote?.appliedPromoCode || null,
    grandTotal: roundMoney(quote?.grandTotal || 0),

    fulfillmentMethod:
      normalizeLowerString(quote?.fulfillmentMethod) || "delivery",
    shippingCountry: normalizeUpperString(quote?.shippingCountry),
    deliveryOption: normalizeDeliveryOption(quote?.deliveryOption),

    readyForCheckout: Boolean(quote?.readyForCheckout),
    hasUnavailableItems: Boolean(quote?.hasUnavailableItems),
    hasPriceChanges: Boolean(quote?.hasPriceChanges),
    itemCount: toNonNegativeInteger(quote?.itemCount, 0),
    distinctItemCount: toNonNegativeInteger(quote?.distinctItemCount, 0),

    messages: {
      shipping: normalizeString(quote?.messages?.shipping),
      total: normalizeString(quote?.messages?.total),
    },
  };
}

function normalizeValidation(payload = {}) {
  return {
    ok: payload?.ok !== false,
    empty: Boolean(payload?.empty),
    issues: Array.isArray(payload?.issues) ? payload.issues : [],
    message: customerSafeText(payload?.message),
  };
}

function getAddKey({ productId = "", variantSku = "", variantType = "" } = {}) {
  return [
    normalizeString(productId),
    normalizeUpperString(variantSku),
    normalizeLowerString(variantType),
  ].join("::");
}

function getRequestAddKey(action = {}) {
  return (
    action?.meta?.addKey ||
    action?.meta?.requestKey ||
    getAddKey(action?.payload || action?.meta)
  );
}

function getResponseAddKey(action = {}) {
  return (
    action?.meta?.addKey ||
    action?.meta?.requestKey ||
    getAddKey(action?.meta || action?.payload)
  );
}

function withPendingAdd(state, key, value) {
  if (!key) return state;

  return {
    ...state,
    pending: {
      ...state.pending,
      addKeys: {
        ...state.pending.addKeys,
        [key]: value,
      },
    },
  };
}

function withPendingItem(state, itemId, value) {
  if (!itemId) return state;

  return {
    ...state,
    pending: {
      ...state.pending,
      itemIds: {
        ...state.pending.itemIds,
        [itemId]: value,
      },
    },
  };
}

function clearPendingAdd(state, key) {
  if (!key) return state;

  const next = { ...state.pending.addKeys };
  delete next[key];

  return {
    ...state,
    pending: {
      ...state.pending,
      addKeys: next,
    },
  };
}

function clearPendingItem(state, itemId) {
  if (!itemId) return state;

  const next = { ...state.pending.itemIds };
  delete next[itemId];

  return {
    ...state,
    pending: {
      ...state.pending,
      itemIds: next,
    },
  };
}

function requestState(state, meta = {}) {
  let nextState = {
    ...state,
    loading: true,
    success: false,
    error: null,
    errorCode: "",
    errorMeta: null,
    message: "",
    issues: [],
  };

  if (meta.pendingFlag) {
    nextState = {
      ...nextState,
      pending: {
        ...nextState.pending,
        [meta.pendingFlag]: true,
      },
    };
  }

  if (meta.addKey) {
    nextState = withPendingAdd(nextState, meta.addKey, true);
  }

  if (meta.itemId) {
    nextState = withPendingItem(nextState, meta.itemId, true);
  }

  return nextState;
}

function successState(state, payload = {}, lastAction, meta = {}) {
  const hasCartField = hasOwn(payload, "cart");
  const hasCheckoutField = hasOwn(payload, "checkout");

  const normalizedPayloadCart = hasCartField
    ? normalizeCart(payload?.cart)
    : undefined;

  let nextCart = hasCartField ? normalizedPayloadCart : state.cart;

  let nextSummary = state.summary;

  if (hasCartField) {
    nextSummary = normalizedPayloadCart
      ? buildSummaryFromCart(normalizedPayloadCart)
      : createInitialSummary();
  } else if (
    hasOwn(payload, "itemCount") ||
    hasOwn(payload, "subtotal") ||
    hasOwn(payload, "ui") ||
    hasOwn(payload, "items")
  ) {
    nextSummary = buildSummaryFromPayload(payload);
  }

  let nextValidation = state.validation;

  if (lastAction === "VALIDATE") {
    nextValidation = normalizeValidation(payload);
  }

  const nextQuote = hasOwn(payload, "quote")
    ? normalizeQuote(payload.quote)
    : state.quote;

  const nextCheckout = hasCheckoutField
    ? payload?.checkout
      ? normalizeCheckout(payload.checkout)
      : null
    : nextCart
      ? buildCheckoutFromCart(nextCart)
      : state.checkout;

  if (nextCart && hasCheckoutField) {
    nextCart = {
      ...nextCart,
      checkout: nextCheckout,
    };
  }

  let nextState = {
    ...state,

    loading: false,
    success: meta.preserveMeta ? state.success : true,

    error: meta.preserveError ? state.error : null,
    errorCode: meta.preserveError ? state.errorCode : "",
    errorMeta: meta.preserveError ? state.errorMeta : null,

    cart: nextCart,
    summary: nextSummary,
    validation: nextValidation,
    quote: nextQuote,
    checkout: nextCheckout,

    message: meta.preserveMeta
      ? state.message
      : customerSafeText(payload?.message),

    issues: meta.preserveMeta
      ? state.issues
      : Array.isArray(payload?.issues)
        ? payload.issues
        : [],

    lastAction: meta.preserveMeta ? state.lastAction : lastAction,
  };

  if (meta.pendingFlag) {
    nextState = {
      ...nextState,
      pending: {
        ...nextState.pending,
        [meta.pendingFlag]: false,
      },
    };
  }

  if (meta.addKey) {
    nextState = clearPendingAdd(nextState, meta.addKey);
  }

  if (meta.itemId) {
    nextState = clearPendingItem(nextState, meta.itemId);
  }

  return nextState;
}

function failState(state, payload, lastAction, meta = {}) {
  const errorMessage =
    typeof payload === "string"
      ? customerSafeText(payload) || "Request failed"
      : customerSafeText(payload?.message) || "Request failed";

  const errorCode =
    typeof payload === "object" && payload?.code
      ? normalizeString(payload.code)
      : "";

  let nextState = {
    ...state,
    loading: false,
    success: false,
    error: errorMessage,
    errorCode,
    errorMeta: typeof payload === "object" ? payload : null,
    message: "",
    issues: Array.isArray(payload?.issues) ? payload.issues : [],
    lastAction,
  };

  if (meta.pendingFlag) {
    nextState = {
      ...nextState,
      pending: {
        ...nextState.pending,
        [meta.pendingFlag]: false,
      },
    };
  }

  if (meta.addKey) {
    nextState = clearPendingAdd(nextState, meta.addKey);
  }

  if (meta.itemId) {
    nextState = clearPendingItem(nextState, meta.itemId);
  }

  return nextState;
}

/* -------------------------------------------------------------------------- */
/*                                  Reducer                                   */
/* -------------------------------------------------------------------------- */

export const cartReducer = (state = initialState, action) => {
  switch (action.type) {
    /* ------------------------------- requests ------------------------------ */

    case actionTypes.CART_GET_REQUEST:
      return requestState(state);

    case actionTypes.CART_SUMMARY_REQUEST:
      return requestState(state, { pendingFlag: "summary" });

    case actionTypes.CART_VALIDATE_REQUEST:
      return requestState(state, { pendingFlag: "validate" });

    case actionTypes.CART_QUOTE_REQUEST:
      return requestState(state, { pendingFlag: "quote" });

    case actionTypes.CART_REFRESH_REQUEST:
      return requestState(state, { pendingFlag: "refresh" });

    case actionTypes.CART_ADD_ITEM_REQUEST:
      return requestState(state, { addKey: getRequestAddKey(action) });

    case actionTypes.CART_SET_ITEM_QTY_REQUEST:
      return requestState(state, { itemId: action.payload?.itemId });

    case actionTypes.CART_INCREMENT_ITEM_REQUEST:
      return requestState(state, { itemId: action.payload?.itemId });

    case actionTypes.CART_DECREMENT_ITEM_REQUEST:
      return requestState(state, { itemId: action.payload?.itemId });

    case actionTypes.CART_REMOVE_ITEM_REQUEST:
      return requestState(state, { itemId: action.payload?.itemId });

    case actionTypes.CART_CLEAR_REQUEST:
      return requestState(state, { pendingFlag: "clear" });

    case actionTypes.CART_REPLACE_REQUEST:
      return requestState(state, { pendingFlag: "replace" });

    case actionTypes.CART_MERGE_REQUEST:
      return requestState(state, { pendingFlag: "merge" });

    case actionTypes.CART_APPLY_PROMO_CODE_REQUEST:
    case actionTypes.CART_REMOVE_PROMO_CODE_REQUEST:
      return requestState(state, { pendingFlag: "promoCode" });

    case actionTypes.CART_REMOVE_UNAVAILABLE_REQUEST:
      return requestState(state, { pendingFlag: "cleanupUnavailable" });

    case actionTypes.CART_REMOVE_PRICE_CHANGED_REQUEST:
      return requestState(state, { pendingFlag: "cleanupPriceChanged" });

    case actionTypes.CART_GET_CHECKOUT_REQUEST:
      return requestState(state, { pendingFlag: "checkout" });

    case actionTypes.CART_SAVE_FULFILLMENT_METHOD_REQUEST:
      return requestState(state, { pendingFlag: "checkoutFulfillment" });

    case actionTypes.CART_SAVE_CUSTOMER_DETAILS_REQUEST:
      return requestState(state, { pendingFlag: "checkoutCustomer" });

    case actionTypes.CART_SAVE_SHIPPING_ADDRESS_REQUEST:
      return requestState(state, { pendingFlag: "checkoutShippingAddress" });

    case actionTypes.CART_SAVE_PICKUP_DETAILS_REQUEST:
      return requestState(state, { pendingFlag: "checkoutPickupDetails" });

    case actionTypes.CART_SAVE_BILLING_OPTIONS_REQUEST:
      return requestState(state, { pendingFlag: "checkoutBilling" });

    case actionTypes.CART_SAVE_CHECKOUT_ACKNOWLEDGEMENTS_REQUEST:
      return requestState(state, { pendingFlag: "checkoutAcknowledgements" });

    case actionTypes.CART_SAVE_PAYMENT_METHOD_REQUEST:
      return requestState(state, { pendingFlag: "checkoutPayment" });

    case actionTypes.CART_SAVE_CHECKOUT_NOTES_REQUEST:
      return requestState(state, { pendingFlag: "checkoutNotes" });

    case actionTypes.CART_RESET_CHECKOUT_REQUEST:
      return requestState(state, { pendingFlag: "checkout" });

    /* ------------------------------- success ------------------------------- */

    case actionTypes.CART_GET_SUCCESS:
      return successState(state, action.payload, "GET");

    case actionTypes.CART_SUMMARY_SUCCESS:
      return successState(state, action.payload, "SUMMARY", {
        pendingFlag: "summary",
      });

    case actionTypes.CART_VALIDATE_SUCCESS:
      return successState(state, action.payload, "VALIDATE", {
        pendingFlag: "validate",
      });

    case actionTypes.CART_QUOTE_SUCCESS:
      return successState(state, action.payload, "QUOTE", {
        pendingFlag: "quote",
      });

    case actionTypes.CART_REFRESH_SUCCESS:
      return successState(state, action.payload, "REFRESH", {
        pendingFlag: action.meta?.silent ? "" : "refresh",
        preserveError: Boolean(action.meta?.preserveError),
        preserveMeta: Boolean(action.meta?.preserveMeta),
      });

    case actionTypes.CART_ADD_ITEM_SUCCESS:
      return successState(state, action.payload, "ADD_ITEM", {
        addKey: getResponseAddKey(action),
      });

    case actionTypes.CART_SET_ITEM_QTY_SUCCESS:
      return successState(state, action.payload, "SET_ITEM_QTY", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_INCREMENT_ITEM_SUCCESS:
      return successState(state, action.payload, "INCREMENT_ITEM", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_DECREMENT_ITEM_SUCCESS:
      return successState(state, action.payload, "DECREMENT_ITEM", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_REMOVE_ITEM_SUCCESS:
      return successState(state, action.payload, "REMOVE_ITEM", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_CLEAR_SUCCESS:
      return successState(state, action.payload, "CLEAR", {
        pendingFlag: "clear",
      });

    case actionTypes.CART_REPLACE_SUCCESS:
      return successState(state, action.payload, "REPLACE", {
        pendingFlag: "replace",
      });

    case actionTypes.CART_MERGE_SUCCESS:
      return successState(state, action.payload, "MERGE", {
        pendingFlag: "merge",
      });

    case actionTypes.CART_APPLY_PROMO_CODE_SUCCESS:
      return successState(state, action.payload, "APPLY_PROMO_CODE", {
        pendingFlag: "promoCode",
      });

    case actionTypes.CART_REMOVE_PROMO_CODE_SUCCESS:
      return successState(state, action.payload, "REMOVE_PROMO_CODE", {
        pendingFlag: "promoCode",
      });

    case actionTypes.CART_REMOVE_UNAVAILABLE_SUCCESS:
      return successState(state, action.payload, "REMOVE_UNAVAILABLE", {
        pendingFlag: "cleanupUnavailable",
      });

    case actionTypes.CART_REMOVE_PRICE_CHANGED_SUCCESS:
      return successState(state, action.payload, "REMOVE_PRICE_CHANGED", {
        pendingFlag: "cleanupPriceChanged",
      });

    case actionTypes.CART_GET_CHECKOUT_SUCCESS:
      return successState(state, action.payload, "GET_CHECKOUT", {
        pendingFlag: "checkout",
      });

    case actionTypes.CART_SAVE_FULFILLMENT_METHOD_SUCCESS:
      return successState(state, action.payload, "SAVE_FULFILLMENT_METHOD", {
        pendingFlag: "checkoutFulfillment",
      });

    case actionTypes.CART_SAVE_CUSTOMER_DETAILS_SUCCESS:
      return successState(state, action.payload, "SAVE_CUSTOMER_DETAILS", {
        pendingFlag: "checkoutCustomer",
      });

    case actionTypes.CART_SAVE_SHIPPING_ADDRESS_SUCCESS:
      return successState(state, action.payload, "SAVE_SHIPPING_ADDRESS", {
        pendingFlag: "checkoutShippingAddress",
      });

    case actionTypes.CART_SAVE_PICKUP_DETAILS_SUCCESS:
      return successState(state, action.payload, "SAVE_PICKUP_DETAILS", {
        pendingFlag: "checkoutPickupDetails",
      });

    case actionTypes.CART_SAVE_BILLING_OPTIONS_SUCCESS:
      return successState(state, action.payload, "SAVE_BILLING_OPTIONS", {
        pendingFlag: "checkoutBilling",
      });

    case actionTypes.CART_SAVE_CHECKOUT_ACKNOWLEDGEMENTS_SUCCESS:
      return successState(
        state,
        action.payload,
        "SAVE_CHECKOUT_ACKNOWLEDGEMENTS",
        { pendingFlag: "checkoutAcknowledgements" },
      );

    case actionTypes.CART_SAVE_PAYMENT_METHOD_SUCCESS:
      return successState(state, action.payload, "SAVE_PAYMENT_METHOD", {
        pendingFlag: "checkoutPayment",
      });

    case actionTypes.CART_SAVE_CHECKOUT_NOTES_SUCCESS:
      return successState(state, action.payload, "SAVE_CHECKOUT_NOTES", {
        pendingFlag: "checkoutNotes",
      });

    case actionTypes.CART_RESET_CHECKOUT_SUCCESS:
      return successState(state, action.payload, "RESET_CHECKOUT", {
        pendingFlag: "checkout",
      });

    /* -------------------------------- fails -------------------------------- */

    case actionTypes.CART_GET_FAIL:
      return failState(state, action.payload, "GET");

    case actionTypes.CART_SUMMARY_FAIL:
      return failState(state, action.payload, "SUMMARY", {
        pendingFlag: "summary",
      });

    case actionTypes.CART_VALIDATE_FAIL:
      return failState(state, action.payload, "VALIDATE", {
        pendingFlag: "validate",
      });

    case actionTypes.CART_QUOTE_FAIL:
      return failState(state, action.payload, "QUOTE", {
        pendingFlag: "quote",
      });

    case actionTypes.CART_REFRESH_FAIL:
      return failState(state, action.payload, "REFRESH", {
        pendingFlag: "refresh",
      });

    case actionTypes.CART_ADD_ITEM_FAIL:
      return failState(state, action.payload, "ADD_ITEM", {
        addKey: getResponseAddKey(action),
      });

    case actionTypes.CART_SET_ITEM_QTY_FAIL:
      return failState(state, action.payload, "SET_ITEM_QTY", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_INCREMENT_ITEM_FAIL:
      return failState(state, action.payload, "INCREMENT_ITEM", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_DECREMENT_ITEM_FAIL:
      return failState(state, action.payload, "DECREMENT_ITEM", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_REMOVE_ITEM_FAIL:
      return failState(state, action.payload, "REMOVE_ITEM", {
        itemId: action.meta?.itemId,
      });

    case actionTypes.CART_CLEAR_FAIL:
      return failState(state, action.payload, "CLEAR", {
        pendingFlag: "clear",
      });

    case actionTypes.CART_REPLACE_FAIL:
      return failState(state, action.payload, "REPLACE", {
        pendingFlag: "replace",
      });

    case actionTypes.CART_MERGE_FAIL:
      return failState(state, action.payload, "MERGE", {
        pendingFlag: "merge",
      });

    case actionTypes.CART_APPLY_PROMO_CODE_FAIL:
      return failState(state, action.payload, "APPLY_PROMO_CODE", {
        pendingFlag: "promoCode",
      });

    case actionTypes.CART_REMOVE_PROMO_CODE_FAIL:
      return failState(state, action.payload, "REMOVE_PROMO_CODE", {
        pendingFlag: "promoCode",
      });

    case actionTypes.CART_REMOVE_UNAVAILABLE_FAIL:
      return failState(state, action.payload, "REMOVE_UNAVAILABLE", {
        pendingFlag: "cleanupUnavailable",
      });

    case actionTypes.CART_REMOVE_PRICE_CHANGED_FAIL:
      return failState(state, action.payload, "REMOVE_PRICE_CHANGED", {
        pendingFlag: "cleanupPriceChanged",
      });

    case actionTypes.CART_GET_CHECKOUT_FAIL:
      return failState(state, action.payload, "GET_CHECKOUT", {
        pendingFlag: "checkout",
      });

    case actionTypes.CART_SAVE_FULFILLMENT_METHOD_FAIL:
      return failState(state, action.payload, "SAVE_FULFILLMENT_METHOD", {
        pendingFlag: "checkoutFulfillment",
      });

    case actionTypes.CART_SAVE_CUSTOMER_DETAILS_FAIL:
      return failState(state, action.payload, "SAVE_CUSTOMER_DETAILS", {
        pendingFlag: "checkoutCustomer",
      });

    case actionTypes.CART_SAVE_SHIPPING_ADDRESS_FAIL:
      return failState(state, action.payload, "SAVE_SHIPPING_ADDRESS", {
        pendingFlag: "checkoutShippingAddress",
      });

    case actionTypes.CART_SAVE_PICKUP_DETAILS_FAIL:
      return failState(state, action.payload, "SAVE_PICKUP_DETAILS", {
        pendingFlag: "checkoutPickupDetails",
      });

    case actionTypes.CART_SAVE_BILLING_OPTIONS_FAIL:
      return failState(state, action.payload, "SAVE_BILLING_OPTIONS", {
        pendingFlag: "checkoutBilling",
      });

    case actionTypes.CART_SAVE_CHECKOUT_ACKNOWLEDGEMENTS_FAIL:
      return failState(
        state,
        action.payload,
        "SAVE_CHECKOUT_ACKNOWLEDGEMENTS",
        { pendingFlag: "checkoutAcknowledgements" },
      );

    case actionTypes.CART_SAVE_PAYMENT_METHOD_FAIL:
      return failState(state, action.payload, "SAVE_PAYMENT_METHOD", {
        pendingFlag: "checkoutPayment",
      });

    case actionTypes.CART_SAVE_CHECKOUT_NOTES_FAIL:
      return failState(state, action.payload, "SAVE_CHECKOUT_NOTES", {
        pendingFlag: "checkoutNotes",
      });

    case actionTypes.CART_RESET_CHECKOUT_FAIL:
      return failState(state, action.payload, "RESET_CHECKOUT", {
        pendingFlag: "checkout",
      });

    /* -------------------------------- resets ------------------------------- */

    case actionTypes.CART_QUOTE_RESET:
      return {
        ...state,
        quote: createInitialQuote(),
        pending: {
          ...state.pending,
          quote: false,
        },
      };

    case actionTypes.CART_RESET_ERROR:
      return {
        ...state,
        error: null,
        errorCode: "",
        errorMeta: null,
        issues: [],
      };

    case actionTypes.CART_RESET_META:
      return {
        ...state,
        success: false,
        message: "",
        lastAction: "",
      };

    case actionTypes.CART_RESET_STATE:
      return createInitialState();

    default:
      return state;
  }
};

export default cartReducer;
